/* Step 7 — Decision + audit trail. Ported from the prototype's decisionBody().
   Record the outcome with an editable AI-drafted response letter; POST .../decision
   persists the decision, queues the decision-letter email and appends to the FCA
   audit log. The same-day trail is sourced live from GET .../decision (DealAuditEvent).
   Confirming ticks all seven steps and routes back to the pipeline. */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loading, EmptyState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useDealState, useProfile } from '../../state';
import { BASE, levelColor } from '../../helpers';
import { fetchDecision, recordDecision, ok } from '../../api';

export default function Decision({ deal }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const { markDecided } = useDealState();
	const { profile } = useProfile();
	const [D, setD] = useState(null);
	const [status, setStatus] = useState('loading');
	const [choice, setChoice] = useState('advance');
	const [letter, setLetter] = useState('');
	const [recorded, setRecorded] = useState(false);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		setStatus('loading');
		const data = await fetchDecision(deal.id);
		if (!data) { setStatus('error'); return; }
		setD(data);
		const initial = data.outcome || 'advance';
		setChoice(initial);
		setLetter((data.letters && data.letters[initial]) || data.letter || '');
		if (data.outcome) { setRecorded(true); markDecided(deal.id); }
		setStatus('ready');
	}, [deal.id, markDecided]);
	useEffect(() => { load(); }, [load]);

	if (status === 'loading') return <main className="page screen"><Loading label="Loading decision…" /></main>;
	if (status === 'error' || !D) return <main className="page screen"><EmptyState title="No decision workspace yet" sub="Generate the IC memo first — the decision step records the committee's outcome." /></main>;

	const options = D.options || [];
	const letters = D.letters || {};
	const record = D.record || {};
	const trail = D.trail || [];
	const firmShort = (profile && profile.short) || 'the fund';

	const pick = id => { setChoice(id); setLetter(letters[id] || letter); };

	const persist = async () => {
		const res = await recordDecision(deal.id, { outcome: choice, letter });
		if (ok(res)) { setRecorded(true); markDecided(deal.id); return true; }
		return false;
	};

	const saveSend = async () => {
		if (busy) return;
		setBusy(true);
		const done = await persist();
		if (done) { showToast('Response saved and sent to the founder'); await load(); }
		setBusy(false);
	};

	const confirm = async () => {
		if (busy) return;
		setBusy(true);
		// Avoid a duplicate audit event if the response was already recorded above.
		const done = recorded ? true : await persist();
		setBusy(false);
		if (!done) return;
		if (choice === 'advance') {
			showToast('Decision logged — Advanced · response sent');
			setTimeout(() => showToast(`${deal.name} added to ${firmShort}'s Portfolio on Diligentsia`, true), 950);
		} else {
			showToast('Decision logged — Declined · constructive feedback sent');
			setTimeout(() => showToast(`${deal.name} profile released for matching with other VCs`, true), 950);
		}
		setTimeout(() => navigate(`${BASE}/pipeline`), 1200);
	};

	return (
		<main className="page screen">
			<div className="kicker">Step 7 · Decision</div>
			<div className="page__title" style={{ fontSize: 24, marginBottom: 16 }}>Record the outcome</div>

			<div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
				{options.map(o => (
					<div key={o.id} className={`opt ${o.id === choice ? 'is-selected' : ''}`} onClick={() => pick(o.id)}>
						<div className="opt__title">{o.title}<span className="opt__radio" /></div>
						<div className="opt__sub">{o.sub}</div>
					</div>
				))}
			</div>

			<div className="grid grid--split">
				<div className="card">
					<span className="chip chip--blue" style={{ marginBottom: 10 }}>✦ Response drafted by Intelligentsia — edit before sending</span>
					<textarea className="rationale-box" style={{ minHeight: 270 }} value={letter} onChange={e => setLetter(e.target.value)} />
					<button className="pill pill--primary" style={{ marginTop: 12 }} onClick={saveSend} disabled={busy}>{busy ? 'Sending…' : 'Save & send response'}</button>
				</div>
				<div>
					<div className="card" style={{ marginBottom: 12 }}>
						<div className="card__label" style={{ marginBottom: 12 }}>Decision record</div>
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
							<div><div className="terms__label">Outcome</div><div className="terms__value">{choice === 'advance'
								? <span style={{ color: 'var(--dlg-pos)' }}>Advanced</span>
								: <span style={{ color: 'var(--dlg-red-ink)' }}>Declined</span>}</div></div>
							<div><div className="terms__label">Decided by</div><div className="terms__value">{record.decidedBy || '—'}</div></div>
							<div><div className="terms__label">Analysis</div><div className="terms__value">{record.analysis || '—'}</div></div>
							<div><div className="terms__label">Triage</div><div className="terms__value">{record.triage || '—'}</div></div>
						</div>
					</div>
					<div className="card">
						<div className="card__label" style={{ marginBottom: 8 }}>Audit trail</div>
						<div className="trail">
							{trail.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }}>No entries yet.</div>
								: trail.map(([t, text, tone], i) => (
									<div key={i}><time>{t}</time><span className="finding__dot" style={{ background: levelColor(tone === 'green' ? 'green' : tone === 'red' ? 'red' : 'blue') }} /><span dangerouslySetInnerHTML={{ __html: text }} /></div>
								))}
						</div>
					</div>
				</div>
			</div>

			<div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
				<button className="pill pill--green" onClick={confirm} disabled={busy}>Confirm &amp; log decision</button>
				<button className="pill pill--outline" onClick={() => navigate(`${BASE}/pipeline`)}>Back to deal flow</button>
				<span style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }}>Logged in FCA format — outcome, reason, who, when — alongside the gate and triage entries.</span>
			</div>
		</main>
	);
}
